import axios from 'axios';
import { store } from '../store';
import { logout } from '../store/slices/authSlice';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Notification callback - will be set by the app
let notificationCallback: ((message: string, severity: 'error' | 'warning' | 'info') => void) | null = null;

/**
 * Set notification callback for error handling
 */
export const setAxiosNotificationCallback = (
  callback: (message: string, severity: 'error' | 'warning' | 'info') => void
) => {
  notificationCallback = callback;
};

// Request interceptor - add JWT token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = store.getState().auth.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle authentication errors
    if (error.response?.status === 401) {
      // Token expired or invalid
      store.dispatch(logout());
      
      // Show notification if callback is set
      if (notificationCallback) {
        notificationCallback('Your session has expired. Please log in again.', 'error');
      }
      
      // Redirect to login
      window.location.href = '/login';
    }

    // Handle permission errors
    if (error.response?.status === 403) {
      if (notificationCallback) {
        notificationCallback('You do not have permission to perform this action.', 'error');
      }
    }

    // Handle network errors
    if (!error.response) {
      if (notificationCallback) {
        notificationCallback('Network error. Please check your internet connection.', 'error');
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
